import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import OfflineNotification from '../components/OfflineNotification';

import {
  Users, Bell, Settings, Calendar, LogOut, Tent, 
  Clock, CheckSquare, AlertTriangle, Info, RefreshCw, 
  X, TrendingUp, TrendingDown, Activity, Star, Zap, 
  Check, PlusCircle, MoveRight
} from 'lucide-react';
import localforage from 'localforage';
import DailySummaryModal from '../components/DailySummaryModal'; // Import the Daily Summary Modal

// Configuration Constants
const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch:443';

// Navigation Tiles Configuration
const NAVIGATION_TILES = [
  {
    title: 'User Management',
    icon: Users,
    description: 'Manage apprentice status, track attendance',
    color: '#da291c',
    gradient: ['#ef7c3c ', '#e2271e'],
    section: 'users'
  },
  {
    title: 'Alert Management',
    icon: Bell,
    description: 'Create and manage system alerts',
    color: '#de849e',
    gradient: ['#d093a6 ', '#f5d2c2'],
    section: 'alerts'
  },
  {
    title: 'Camp Management',
    icon: Tent,
    description: 'Organize and track camp activities',
    color: '#99b2a7',
    gradient: ['#8d989b ', '#f8edd2'],
    section: 'camps'
  },
  {
    title: 'User Verification',
    icon: CheckSquare,
    description: 'Verify user status and attendance',
    color: '#c48d8a',
    gradient: ['#9c8a95 ', '#f6d6b3'],
    section: 'verification'
  },
  {
    title: 'History Tracking',
    icon: Clock,
    description: 'Comprehensive user activity log',
    color: '#da291c',
    gradient: ['#D42F2F ', '#E67E22'],
    section: 'history'
  },
  {
    title: 'System Settings',
    icon: Settings,
    description: 'Configure system preferences',
    color: '#008077',
    gradient: ['#ecb9a6 ', '#008077'],
    section: 'settings'
  }
];

const DashboardHome = ({ onNavigate }) => {
  // State Management 
   const [isOnline, setIsOnline] = useState(navigator.onLine);

  const [activeCamps, setActiveCamps] = useState([]);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    checkedOutUsers: 0,
    activeAlerts: 0,
    upcomingEvents: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userName, setUserName] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [dailySummaryUsers, setDailySummaryUsers] = useState([]);
  const [showDailySummary, setShowDailySummary] = useState(false);
  const [dailySummaryCamps, setDailySummaryCamps] = useState([]);
  // Responsive Design
  const isMobile = windowWidth <= 768;
  // useEffect(() => {
  //   const handleOnline = () => setIsOnline(true);
  //   const handleOffline = () => setIsOnline(false);

  //   window.addEventListener('online', handleOnline);
  //   window.addEventListener('offline', handleOffline);

  //   return () => {
  //     window.removeEventListener('online', handleOnline);
  //     window.removeEventListener('offline', handleOffline);
  //   };
  // }, []);

  // Window Resize Handler
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch Dashboard Data
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const token = await localforage.getItem('token');
      const userData = await localforage.getItem('user');
      
      if (!token || !userData) {
        throw new Error('Not authenticated');
      }

      const user = JSON.parse(userData);
      setUserName(user.username);
      setProfileImage(user.profileImagePath ?
        `${BASE_URL}${user.profileImagePath}` :
        '/api/placeholder/80/80'
      );

      // Parallel data fetching
      const [campsResponse, alertsResponse, usersResponse] = await Promise.all([
        fetch(`${BASE_URL}/api/camps`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BASE_URL}/api/alerts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BASE_URL}/api/auth/get-all-users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const campsData = await campsResponse.json();
      const alertsData = await alertsResponse.json();
      const usersData = await usersResponse.json();

      const currentDate = new Date();
      const active = campsData.filter(camp => {
        const startDate = new Date(camp.startDate);
        const endDate = new Date(camp.endDate);
        return currentDate >= startDate && currentDate <= endDate;
      });

      const currentAlerts = alertsData.filter(a => new Date(a.endTime) > new Date());

      // Daily Summary Users (active after 22:10 and not checked out)
      const dailySummaryUsers = usersData.users.filter(user => {
        const lastActivity = new Date(user.lastActivityTimestamp);
        const today = new Date();
        const late22_10 = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 10);
        
        return (
          lastActivity > late22_10 && 
          user.status !== 'CHECKEDOUT'
        );
      });

      setDailySummaryUsers(dailySummaryUsers);
      setActiveCamps(active);
      setActiveAlerts(currentAlerts);

      const newStats = {
        totalUsers: usersData.users.length,
        checkedOutUsers: usersData.users.filter(u => u.status === 'CHECKEDOUT').length,
        activeAlerts: currentAlerts.length,
        upcomingEvents: campsData.filter(c => new Date(c.startDate) > new Date()).length
      };

      setStats(newStats);

    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial and periodic data fetch
  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // Styles (with responsive considerations)
  const styles = useMemo(() => ({
    container: {
      padding: isMobile ? '16px' : '32px',
      maxWidth: '3400px',
      margin: '0 auto',
      minHeight: '100vh',
      backgroundColor: '#F8FAFC',
      position: 'relative',
      overflow: 'hidden',
      marginLeft: isMobile ? "-15px" : 'auto',
      width:isMobile ? "100%" : '95%',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',

        borderRadius:"20px",
      marginTop: isMobile ? "12px" : 'auto',
    }, dailySummaryButton: {
      backgroundColor: '#3d3935',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '20px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 6px rgba(59, 130, 246, 0.2)',
      width:isMobile ? "282px" : '65.9%',
      alignItems:"center",
      textAlign:"center",
      

    },
    header: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '60px'
    },
    profileSection: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: isMobile ? '10px' : '20px'
    },
    profileImage: {
      width: isMobile ? '60px' : '76px',
      height: isMobile ? '60px' : '76px',
      borderRadius: '50%',
      objectFit: 'cover',
      border: '2px solid white',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    dashboardGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
      gap: '24px'
    },
    dailySummaryModal: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: isMobile ? '95%' : '600px',
      maxHeight: isMobile ? '90vh' : '80vh',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      padding: isMobile ? '16px' : '32px',
      overflowY: 'auto',
      zIndex: 1000
    }
  }), [isMobile]);

  // Daily Summary Component
  // const DailySummaryModal = () => (
  //   <motion.div 
  //     initial={{ opacity: 0 }}
  //     animate={{ opacity: 1 }}
  //     exit={{ opacity: 0 }}
  //     style={{
  //       position: 'fixed',
  //       top: 0,
  //       left: 0,
  //       right: 0,
  //       bottom: 0,
  //       backgroundColor: 'rgba(0,0,0,0.5)',
  //       display: 'flex',
  //       justifyContent: 'center',
  //       alignItems: 'center',
  //       zIndex: 1000
  //     }}
  //     onClick={() => setShowDailySummary(false)}
  //   >
  //     <motion.div 
  //       style={styles.dailySummaryModal}
  //       initial={{ scale: 0.8, opacity: 0 }}
  //       animate={{ scale: 1, opacity: 1 }}
  //       exit={{ scale: 0.8, opacity: 0 }}
  //       onClick={(e) => e.stopPropagation()}
  //     >
  //       <div style={{ 
  //         display: 'flex', 
  //         justifyContent: 'space-between', 
  //         alignItems: 'center',
  //         marginBottom: '20px'
  //       }}>
  //         <h2 style={{ margin: 0, fontSize: '24px' }}>Daily Summary</h2>
  //         <button 
  //           onClick={() => setShowDailySummary(false)}
  //           style={{
  //             background: 'none',
  //             border: 'none',
  //             fontSize: '24px',
  //             cursor: 'pointer'
  //           }}
  //         >
  //           ×
  //         </button>
  //       </div>
        
  //       <div>
  //         <h3>Users Active After 22:10</h3>
  //         {dailySummaryUsers.length === 0 ? (
  //           <p>No users active after 22:10</p>
  //         ) : (
  //           <motion.ul 
  //             initial="hidden"
  //             animate="visible"
  //             variants={{
  //               hidden: { opacity: 0 },
  //               visible: { 
  //                 opacity: 1,
  //                 transition: { 
  //                   staggerChildren: 0.1 
  //                 } 
  //               }
  //             }}
  //             style={{ 
  //               listStyle: 'none', 
  //               padding: 0 
  //             }}
  //           >
  //             {dailySummaryUsers.map((user, index) => (
  //               <motion.li
  //                 key={user.id}
  //                 variants={{
  //                   hidden: { opacity: 0, x: -20 },
  //                   visible: { opacity: 1, x: 0 }
  //                 }}
  //                 style={{
  //                   display: 'flex',
  //                   justifyContent: 'space-between',
  //                   alignItems: 'center',
  //                   padding: '10px',
  //                   backgroundColor: index % 2 === 0 ? '#f9fafb' : 'white',
  //                   borderRadius: '8px',
  //                   marginBottom: '8px'
  //                 }}
  //               >
  //                 <div>
  //                   <strong>{user.username}</strong>
  //                   <div style={{ fontSize: '0.8em', color: '#6b7280' }}>
  //                     {user.email}
  //                   </div>
  //                 </div>
  //                 <div 
  //                   style={{
  //                     backgroundColor: user.status === 'CHECKEDOUT' ? '#10b981' : '#ef4444',
  //                     color: 'white',
  //                     padding: '4px 8px',
  //                     borderRadius: '4px',
  //                     fontSize: '0.7em'
  //                   }}
  //                 >
  //                   {user.status}
  //                 </div>
  //               </motion.li>
  //             ))}
  //           </motion.ul>
  //         )}
  //       </div>
        
  //     </motion.div>
  //   </motion.div>
  // );

  // Main Render
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{
              repeat: Infinity,
              duration: 1,
              ease: "linear"
            }}
          >
            <RefreshCw size={48} color="#3B82F6" />
          </motion.div>
        </div>
      </div>
    );
  }
  
  // Check if offline
  const isOffline = !navigator.onLine;
  
  return (
    <motion.div 
      style={styles.container}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Offline Notification */}
      {isOffline && (
        <div style={{
          backgroundColor: '#F87171',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          textAlign: 'center',
          marginBottom: '16px'
        }}>
          Sie sind offline. Einige Funktionen sind möglicherweise eingeschränkt.
        </div>
      )}
  
      {/* Daily Summary Modal */}
      <AnimatePresence>
        {showDailySummary && <DailySummaryModal />}
      </AnimatePresence>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.profileSection}>
          <img 
            src={profileImage} 
            alt="Profile" 
            style={styles.profileImage} 
          />
          <div>
            <h1 style={{ 
              margin: 0, 
              fontSize: isMobile ? '18px' : '24px' 
            }}>
              Welcome, {userName}
            </h1>
            <p style={{ 
              margin: 0, 
              color: '#64748B', 
              fontSize: isMobile ? '14px' : '16px' 
            }}>
              Here's your dashboard overview
            </p>
          </div>
        </div>
        <button 
          onClick={() => {
            localforage.removeItem('token');
            window.location.reload();
          }}
          style={{
            backgroundColor: '#EF4444',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: isMobile ? '10px' : 0
          }}
        >
          <LogOut size={20} />
          Sign Out
        </button>
      </div>

      {/* Daily Summary Button */}
      <motion.button
        style={styles.dailySummaryButton}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowDailySummary(true)}
      >
        <Calendar size={20} />
        Show Daily Check-Out Summary
      </motion.button>  <AnimatePresence>
        {showDailySummary && (
          <DailySummaryModal 
            onClose={() => setShowDailySummary(false)}
            camps={dailySummaryCamps}
          />
        )}
      </AnimatePresence>

      {/* Dashboard Grid */}
      <div style={styles.dashboardGrid}>
        {/* Left Section - Active Camps and Alerts */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}
        >
          <h2 style={{ 
            margin: 0, 
            marginBottom: '20px',
            fontSize: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            Active Camps & Alerts
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onNavigate && onNavigate('camps')}
              style={{
                backgroundColor: '#F0F9FF',
                border: '1px solid #BFDBFE',
                color: '#1E40AF',
                padding: '8px 16px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: isMobile ? '100px' : '10px',
                width: isMobile ? '230px' : 'auto',
                marginLeft: isMobile ? '-300px' : 'auto',


              }}
            >
              <PlusCircle size={16} />
              Manage
            </motion.button>
          </h2>

          {/* Active Camps Section */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              margin: 0,
              marginBottom: '16px',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Tent size={20} />
              Current Camps
            </h3>
            {activeCamps.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  backgroundColor: '#F8FAFC',
                  padding: '16px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  color: '#64748B'
                }}
              >
                No active camps at the moment
              </motion.div>
            ) : (
              activeCamps.map((camp, index) => (
                <motion.div
                  key={camp.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  style={{
                    backgroundColor: '#F8FAFC',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <h4 style={{
                      margin: 0,
                      marginBottom: '8px',
                      fontSize: '15px',
                      fontWeight: '600'
                    }}>
                      {camp.title}
                    </h4>
                    <p style={{
                      margin: 0,
                      color: '#64748B',
                      fontSize: '13px'
                    }}>
                      {new Date(camp.startDate).toLocaleDateString()} - 
                      {new Date(camp.endDate).toLocaleDateString()}
                    </p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => onNavigate && onNavigate('camps')}
                    style={{
                      backgroundColor: '#3B82F6',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}
                  >
                    Details
                  </motion.button>
                </motion.div>
              ))
            )}
          </div>

          {/* Active Alerts Section */}
          <div>
            <h3 style={{
              margin: 0,
              marginBottom: '16px',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Bell size={20} />
              Current Alerts
            </h3>
            {activeAlerts.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  backgroundColor: '#F8FAFC',
                  padding: '16px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  color: '#64748B'
                }}
              >
                No active alerts
              </motion.div>
            ) : (
              activeAlerts.map((alert, index) => (
                <motion.div
                  key={alert.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  style={{
                    backgroundColor: alert.priority === 'important' 
                      ? '#FEF3C7' : '#F0F9FF',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: `1px solid ${alert.priority === 'important' 
                      ? '#FDE68A' : '#BFDBFE'}`
                  }}
                >
                  <div>
                    <h4 style={{
                      margin: 0,
                      marginBottom: '8px',
                      fontSize: '15px',
                      fontWeight: '600',
                      color: alert.priority === 'important' 
                        ? '#92400E' : '#1E40AF'
                    }}>
                      {alert.title}
                    </h4>
                    <p style={{
                      margin: 0,
                      color: alert.priority === 'important' 
                        ? '#78350F' : '#1E3A8A',
                      fontSize: '13px'
                    }}>
                      {alert.description}
                    </p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => onNavigate && onNavigate('alerts')}
                    style={{
                      backgroundColor: alert.priority === 'important' 
                        ? '#F59E0B' : '#3B82F6',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}
                  >
                    Manage
                  </motion.button>
                </motion.div>
              ))
            )}
          </div>
        </motion.div>

        {/* Right Section - Statistics */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}
        >
          <h2 style={{ 
            margin: 0, 
            marginBottom: '20px',
            fontSize: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            System Statistics
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onNavigate && onNavigate('users')}
              style={{
                backgroundColor: '#F0F9FF',
                border: '1px solid #BFDBFE',
                color: '#1E40AF',
                padding: '8px 16px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Activity size={16} />
              Insights
            </motion.button>
          </h2>

          {[
            {
              icon: Users,
              label: 'Total Users',
              value: stats.totalUsers,
              color: '#4F46E5'
            },
            {
              icon: CheckSquare,
              label: 'Checked Out',
              value: stats.checkedOutUsers,
              color: '#10B981'
            },
            {
              icon: Bell,
              label: 'Active Alerts',
              value: stats.activeAlerts,
              color: '#7C3AED'
            },
            {
              icon: Calendar,
              label: 'Upcoming Events',
              value: stats.upcomingEvents,
              color: '#EC4899'
            }
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#F8FAFC',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '12px'
              }}
              whileHover={{ scale: 1.02 }}
            >
              <div
                style={{
                  backgroundColor: stat.color + '20',
                  color: stat.color,
                  padding: '12px',
                  borderRadius: '8px',
                  marginRight: '16px'
                }}
              >
                <stat.icon size={24} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '14px',
                  color: '#64748B',
                  marginBottom: '4px'
                }}>
                  {stat.label}
                </div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1E293B'
                }}>
                  {stat.value}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Navigation Grid */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '24px',
          marginTop: '24px'
        }}
      >
        {NAVIGATION_TILES.map((tile, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ 
              scale: 1.05,
              boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
            }}
            style={{
              background: `linear-gradient(135deg, ${tile.gradient[0]}, ${tile.gradient[1]})`,
              color: 'white',
              borderRadius: '16px',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={() => onNavigate && onNavigate(tile.section)}
          >
            <div style={{ 
              position: 'absolute', 
              top: '-50%', 
              left: '-50%', 
              width: '200%', 
              height: '200%', 
              background: 'rgba(255,255,255,0.1)', 
              transform: 'rotate(45deg)' 
            }} />
            <tile.icon size={32} />
            <div>
              <h3 style={{ 
                margin: 0, 
                marginTop: '16px', 
                fontSize: '18px', 
                fontWeight: '600' 
              }}>
                {tile.title}
              </h3>
              <p style={{ 
                margin: 0, 
                marginTop: '8px', 
                fontSize: '14px', 
                opacity: 0.7 
              }}>
                {tile.description}
              </p>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.7 }}
        style={{
          marginTop: '32px',
          padding: '24px',
          backgroundColor: '#F8FAFC',
          borderTop: '1px solid #E2E8F0',
          textAlign: 'center',
          color: '#64748B',
          fontSize: '14px'
        }}
      >
        <p style={{ margin: 0 }}>
          © {new Date().getFullYear()} Admin Dashboard. All rights reserved.
        </p>
      </motion.footer>
    </motion.div>
  );
};

export default DashboardHome;